import { PERMISSION_COSTS_READ, defineModule, definePermission } from '@shared/contract'

// Plattform: Hülle der Oberfläche (Suche, Benachrichtigungen, Aufgaben, Ansichten), Audit-Protokoll und die
// Verwaltung des Betriebs: Personen, Rollen und Rechte, Unternehmen, Anbindungen, Datenschutz, System.
// Rechte der Verwaltung gelten für den ganzen Betrieb (locationScoped: false); wer Zugänge, Rollen, Schlüssel oder
// Löschungen verwaltet, hält ein sicherheitskritisches Recht (sensitive) – jede Aktion damit verlangt eine frische
// Anmeldung (server/admin/support.ts: requireFreshAuth).
export default defineModule({
  id: 'platform',
  title: 'Plattform',
  description:
    'Suche, Benachrichtigungen, Aufgaben und gespeicherte Ansichten – und die Verwaltung: Personen, Rollen und Rechte, Unternehmen, Anbindungen, Audit-Protokoll, Datenschutz, System.',
  permissions: [
    definePermission({
      key: 'platform.audit.read',
      label: 'Audit-Protokoll lesen',
      group: 'Verwaltung',
      description: 'Wer hat wann was geändert – je Standort.',
    }),
    definePermission({
      key: 'platform.system.manage',
      label: 'System verwalten',
      group: 'Verwaltung',
      description: 'Wartungsläufe für den ganzen Betrieb starten, zum Beispiel den Suchindex neu aufbauen.',
      locationScoped: false,
    }),
    definePermission({ key: 'platform.task.read', label: 'Aufgaben sehen', group: 'Aufgaben' }),
    definePermission({ key: 'platform.task.resolve', label: 'Aufgaben erledigen', group: 'Aufgaben' }),
    definePermission({
      key: PERMISSION_COSTS_READ,
      label: 'Einstandspreise und Roherträge sehen',
      group: 'Auswertung',
      description: 'Ohne dieses Recht fehlen Einkaufspreise, Einstandswerte und Margen in allen Ausgaben.',
      locationScoped: false,
    }),
    // ---- Verwaltung: Personen ----
    definePermission({
      key: 'platform.user.read',
      label: 'Personen und Zugänge ansehen',
      group: 'Verwaltung',
      description: 'Wer zum Betrieb gehört, mit welchen Rollen an welchen Standorten, und welche Einladungen offen sind.',
      locationScoped: false,
    }),
    definePermission({
      key: 'platform.user.manage',
      label: 'Personen einladen, sperren und Rollen zuweisen',
      group: 'Verwaltung',
      description:
        'Einladungen ausstellen und zurückziehen, Zugänge sperren und freigeben, Rollen je Standort zuweisen. Vergeben lässt sich nur, was man selbst hält.',
      locationScoped: false,
      sensitive: true,
    }),
    // ---- Verwaltung: Rollen ----
    definePermission({
      key: 'platform.role.read',
      label: 'Rollen und Rechte ansehen',
      group: 'Verwaltung',
      description: 'Die Matrix Recht × Rolle und die Auskunft „Was darf diese Person?“.',
      locationScoped: false,
    }),
    definePermission({
      key: 'platform.role.manage',
      label: 'Rollen und Rechte ändern',
      group: 'Verwaltung',
      description: 'Rollen anlegen, kopieren und löschen, Rechte und Grenzen einer Rolle ändern.',
      locationScoped: false,
      sensitive: true,
    }),
    // ---- Verwaltung: Unternehmen ----
    definePermission({
      key: 'platform.settings.read',
      label: 'Einstellungen des Betriebs ansehen',
      group: 'Verwaltung',
      description: 'Firmendaten, Erscheinungsbild, Funktionen, Nummernkreise und Zusatzfelder ansehen.',
      locationScoped: false,
    }),
    definePermission({
      key: 'platform.settings.manage',
      label: 'Einstellungen des Betriebs ändern',
      group: 'Verwaltung',
      description: 'Firmendaten und Belegtexte, Logo und Hausfarbe, Funktionen des Branchenprofils, Nummernkreise, Zusatzfelder.',
      locationScoped: false,
    }),
    // ---- Verwaltung: Anbindungen ----
    definePermission({
      key: 'platform.integration.read',
      label: 'Anbindungen ansehen',
      group: 'Verwaltung',
      description: 'Technische Zugänge mit ihren Schlüsseln (ohne Klartext), Webhooks und deren Zustellungen.',
      locationScoped: false,
    }),
    definePermission({
      key: 'platform.integration.manage',
      label: 'Anbindungen verwalten',
      group: 'Verwaltung',
      description: 'Technische Zugänge anlegen, API-Schlüssel ausstellen und widerrufen, Webhooks einrichten.',
      locationScoped: false,
      sensitive: true,
    }),
    // ---- Verwaltung: E-Mail und Anmeldung (docs/anmeldung/SPEZIFIKATION.md §8.2) ----
    definePermission({
      key: 'platform.mail.read',
      label: 'Postausgang und E-Mails des Betriebs ansehen',
      group: 'Verwaltung',
      description: 'Alle E-Mails des Betriebs mit Zustand, Empfängern und Antwort des Mailservers; Absender, Vorlagen und Sperrliste ansehen.',
      locationScoped: false,
    }),
    definePermission({
      key: 'platform.mail.manage',
      label: 'Absender, Vorlagen und Sperrliste verwalten',
      group: 'Verwaltung',
      description: 'Absender und E-Mail-Vorlagen pflegen, Adressen sperren, E-Mails abbrechen, erneut senden oder als unzustellbar markieren.',
      locationScoped: false,
    }),
    definePermission({
      key: 'platform.mail.inbox',
      label: 'E-Mail-Eingang einrichten und eingegangene E-Mails lesen',
      group: 'Verwaltung',
      description:
        'Postfach für eingehende E-Mails anbinden (Server, Benutzer, Kennwort), Verbindung testen, alle eingegangenen E-Mails lesen und E-Mail-Dateien (.eml) einlesen.',
      locationScoped: false,
      sensitive: true,
    }),
    definePermission({
      key: 'platform.security.manage',
      label: 'Anmelderichtlinie festlegen (Zwei-Faktor-Pflicht, vertraute Geräte)',
      group: 'Verwaltung',
      description: 'Wer die Zwei-Faktor-Anmeldung braucht, wie lange die Übergangsfrist läuft, wie lange Geräte vertraut bleiben und ob Links zum Weitergeben erlaubt sind.',
      locationScoped: false,
      sensitive: true,
    }),
    // ---- Verwaltung: Datenschutz ----
    definePermission({
      key: 'platform.privacy.manage',
      label: 'Datenschutz: Auskunft und Löschung',
      group: 'Verwaltung',
      description:
        'Auskunft über die gespeicherten Daten einer Person erstellen und den Personenbezug anonymisierter Geschäftspartner nach Fristablauf entfernen.',
      locationScoped: false,
      sensitive: true,
    }),
  ],
})
